import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type DebriefStage,
  type GetDebriefFactsApiRequest,
  type GetDebriefFactsApiResponse,
} from '@momentum/types';
import {
  createInitialDescribeState,
  describeReducer,
} from '../../describe-stage/utils/describe.reducer';
import type {
  DescribeAction,
  DescribeState,
} from '../../describe-stage/types/describe.types';
import { describeStrings } from '../../describe-stage/utils/describe.strings';
import { toAnswersPayload } from '../../describe-stage/utils/toAnswersPayload';
import {
  debriefFactsReducer,
  initialDebriefFactsState,
  type DebriefFactsAction,
  type DebriefFactsState,
} from './debriefFactsReducer';
import { debriefV2Service } from '../services/debrief-v2.service';
import {
  deepDiveReducer,
  initialDeepDiveState,
} from '../../deep-dive-stage/utils/deepDive.reducer';
import type {
  DeepDiveAction,
  DeepDiveState,
} from '../../deep-dive-stage/types/deepDive.types';
import type { EditableFact } from '../types';

interface SliceController<TState, TAction> {
  state: TState;
  dispatch: Dispatch<TAction>;
}

interface DebriefV2StateContextValue {
  describe: SliceController<DescribeState, DescribeAction>;
  facts: SliceController<DebriefFactsState, DebriefFactsAction> & {
    isLoading: boolean;
  };
  deepDive: SliceController<DeepDiveState, DeepDiveAction>;
}

const DebriefV2StateContext = createContext<DebriefV2StateContextValue | null>(
  null
);

interface DebriefV2StateProviderProps {
  sessionId: string;
  children: ReactNode;
}

export function DebriefV2StateProvider({
  sessionId,
  children,
}: DebriefV2StateProviderProps) {
  const [describeState, describeDispatch] = useReducer(
    describeReducer,
    sessionId,
    createInitialDescribeState
  );
  const [factsState, factsDispatch] = useReducer(
    debriefFactsReducer,
    initialDebriefFactsState
  );
  const [deepDiveState, deepDiveDispatch] = useReducer(
    deepDiveReducer,
    initialDeepDiveState
  );

  const { data: factsData, isLoading: isFactsLoading } =
    useDebriefFactsQuery(describeState);

  useEffect(() => {
    if (factsData && !factsState.isHydrated) {
      factsDispatch({ type: 'HYDRATE', payload: factsData.facts });
    }
  }, [factsData, factsState.isHydrated]);

  const value = useMemo<DebriefV2StateContextValue>(
    () => ({
      describe: { state: describeState, dispatch: describeDispatch },
      facts: {
        state: factsState,
        dispatch: factsDispatch,
        isLoading: isFactsLoading || !factsState.isHydrated,
      },
      deepDive: { state: deepDiveState, dispatch: deepDiveDispatch },
    }),
    [describeState, factsState, isFactsLoading, deepDiveState]
  );

  return (
    <DebriefV2StateContext.Provider value={value}>
      {children}
    </DebriefV2StateContext.Provider>
  );
}

export function useDebriefV2State(): DebriefV2StateContextValue {
  const value = useContext(DebriefV2StateContext);
  if (!value) {
    throw new Error(
      'useDebriefV2State must be used within a DebriefV2StateProvider'
    );
  }
  return value;
}

function debriefFactsQueryKey(
  sessionId: string,
  payload: GetDebriefFactsApiRequest | null
) {
  return ['debrief-v2', 'facts', sessionId, payload] as const;
}

function useDebriefFactsQuery(describeState: DescribeState) {
  const payload = describeState.isComplete
    ? toAnswersPayload(describeState)
    : null;

  return useQuery<GetDebriefFactsApiResponse, Error>({
    queryKey: debriefFactsQueryKey(describeState.sessionId, payload),
    queryFn: payload
      ? async () => {
          try {
            return await debriefV2Service.getFacts(payload);
          } catch (error) {
            toast.error(describeStrings.situationCaptured.factsError);
            throw error;
          }
        }
      : skipToken,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function groupFactsByStage(
  facts: EditableFact[]
): Partial<Record<DebriefStage, EditableFact[]>> {
  const factsByStage: Partial<Record<DebriefStage, EditableFact[]>> = {};
  for (const fact of facts) {
    const bucket = factsByStage[fact.stage] ?? [];
    bucket.push(fact);
    factsByStage[fact.stage] = bucket;
  }
  return factsByStage;
}
